import React, { useCallback, useEffect, useRef } from 'react';

import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import { TRACKING_EVENTS } from '@futrli/predict/core/constants';
import { trackEvent } from '@futrli/predict/core/tracker';
import { useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard/shared/useDialogWizardState';

import { getInviteUserErrorMessage } from '../utils';
import { InviteUserFormWizardScreen } from './screens/invite-user-form-wizard-screen';
import { useInviteUserContext } from '../context/invite-user-context';
import { UserInvitedWizardScreen } from './screens/user-invited-wizard-screen';
import { UserInvitingWizardScreen } from './screens/user-inviting-wizard-screen';

interface InviteUserContainerProps {
  onClose: () => void;
}

export const InviteUserContainer = ({ onClose }: InviteUserContainerProps) => {
  const { form, view } = useInviteUserContext();
  const hasTrackedInvitation = useRef(false);

  const handleSubmit = useCallback(() => {
    view.inviteUser({
      email: form.email,
      fullName: form.fullName,
      roleId: form.roleId,
      selectedOrganisations: form.selectedOrganisations
    });
  }, [form, view]);

  const { screen, handleAction } = useDialogWizardState({
    error: view.state.errorCreatingInvitation,
    isLoading: view.state.isCreatingInvitation,
    onAction: handleSubmit,
    onClose,
    validate: form.validate
  });

  useEffect(() => {
    if (screen === 'success' && !hasTrackedInvitation.current) {
      hasTrackedInvitation.current = true;
      trackEvent(TRACKING_EVENTS.INVITED_USER);
    }
  }, [screen]);

  if (view.state.isLoading) {
    return <LoadingWizardScreen />;
  }

  switch (screen) {
    case 'loading':
      return <UserInvitingWizardScreen />;
    case 'success':
      return <UserInvitedWizardScreen onClose={onClose} />;
    default:
      return (
        <InviteUserFormWizardScreen
          error={getInviteUserErrorMessage(view.state.errorCreatingInvitation || view.state.errorLoading)}
          onClose={onClose}
          onInvite={handleAction}
        />
      );
  }
};
